import React, { useState } from "react";
import { Table } from "reactstrap";

const FreightComparison = ({ data }) => {
  // Unique keys for every quote column
  const carrierKeys = data?.map((quote, idx) => {
    const carrierName = quote.fclInquiryResults[0]?.carrierName;
    return carrierName ? `${carrierName}-${idx}` : `Quote-${idx + 1}`;
  }) || [];

  const tenantNames = data?.map((q, idx) =>
    q.fclInquiryResults[0]?.tenantName || `Tenant-${idx + 1}`
  ) || [];

  // Section headers → tariff header names
  const sections = {
    "Origin Inland Charges": "ORIGIN_INLAND_CHARGES",
    "Origin Port & Local Charges": "ORIGIN_LOCAL_PORT_CHARGES",
    "Freight": "FREIGHT_CHARGES",
    "Destination Port & Local Charges": "DESTINATION_LOCAL_PORT_CHARGES",
    "Destination Inland Charges": "DESTINATION_INLAND_CHARGES",
  };

  // Collect all rows (unique component descriptions) per section
  const sectionRows = {};
  Object.keys(sections).forEach((title) => {
    sectionRows[title] = new Set();
    data?.forEach((quote) => {
      const carrier = quote.fclInquiryResults[0];
      const tariff = carrier?.tariffDetails?.find(
        (t) => t.header === sections[title]
      );
      (tariff?.fclTariffBreakDowns || []).forEach((c) => {
        const rowLabel = c.componentDescription || c.component;
        sectionRows[title].add(rowLabel);
      });
    });
  });

  // Subtotals per section per quote
  const sectionSubtotals = {};
  Object.keys(sections).forEach((title) => {
    sectionSubtotals[title] = {};
    data?.forEach((quote, idx) => {
      const carrier = quote.fclInquiryResults[0];
      const key = carrierKeys[idx];

      const tariff = carrier?.tariffDetails?.find(
        (t) => t.header === sections[title]
      );
      const subtotal = (tariff?.fclTariffBreakDowns || []).reduce(
        (sum, c) => sum + (c.amount || 0),
        0
      );

      sectionSubtotals[title][key] = subtotal;
    });
  });

  // Grand totals per quote
  const totals = {};
  data?.forEach((quote, idx) => {
    const carrier = quote.fclInquiryResults[0];
    const key = carrierKeys[idx];

    const total =
      (carrier?.tariffDetails || [])
        .flatMap((t) => t.fclTariffBreakDowns || [])
        .reduce((sum, x) => sum + (x.amount || 0), 0);

    totals[key] = total;
  });

  // Expand/collapse state for each section
  const [expanded, setExpanded] = useState(
    Object.keys(sections).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {})
  );

  const toggleSection = (section) =>
    setExpanded((prev) => ({ ...prev, [section]: !prev[section] }));

  return (
    <div className="mt-4">
      <Table bordered responsive>
        <thead>
          <tr>
            <th>Details</th>
            {tenantNames.map((name, idx) => (
              <th key={carrierKeys[idx]}>{name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Static carrier/route info */}
          <tr>
            <td>Carrier</td>
            {data?.map((q, idx) => (
              <td key={carrierKeys[idx]}>
                {q.fclInquiryResults[0]?.carrierName || "-"}
              </td>
            ))}
          </tr>
          <tr>
            <td>POL</td>
            {data?.map((q, idx) => (
              <td key={carrierKeys[idx]}>
                {q.fclInquiryResults[0]?.originName || "-"}
              </td>
            ))}
          </tr>
          <tr>
            <td>POD</td>
            {data?.map((q, idx) => (
              <td key={carrierKeys[idx]}>
                {q.fclInquiryResults[0]?.destinationName || "-"}
              </td>
            ))}
          </tr>
          <tr>
            <td>Transit Time</td>
            {data?.map((q, idx) => (
              <td key={carrierKeys[idx]}>
                {q.fclInquiryResults[0]?.oceanTransitTime || "-"}
              </td>
            ))}
          </tr>
          <tr>
            <td>Via</td>
            {data?.map((q, idx) => (
              <td key={carrierKeys[idx]}>
                {q.fclInquiryResults[0]?.via || "-"}
              </td>
            ))}
          </tr>
          <tr>
            <td>Incoterms</td>
            {data?.map((q, idx) => (
              <td key={carrierKeys[idx]}>
                {q.fclInquiryResults[0]?.incoterm || "-"}
              </td>
            ))}
          </tr>

          {/* Dynamic charge sections */}
          {Object.keys(sections).map((section) => (
            <React.Fragment key={section}>
              {/* Section header with toggle */}
              <tr
                className="fw-bold bg-light"
                style={{ cursor: "pointer" }}
                onClick={() => toggleSection(section)}
              >
                <td>
                  {expanded[section] ? "▼ " : "▶ "} {section}
                </td>
                {carrierKeys.map((key) => (
                  <td key={key}>
                    {sectionSubtotals[section][key] ?? "-"}
                  </td>
                ))}
              </tr>

              {/* Charges inside section */}
              {expanded[section] &&
                [...sectionRows[section]].map((row) => (
                  <tr key={row}>
                    <td>{row}</td>
                    {data?.map((quote, idx) => {
                      const carrier = quote.fclInquiryResults[0];
                      const key = carrierKeys[idx];
                      const tariff = carrier?.tariffDetails?.find(
                        (t) => t.header === sections[section]
                      );
                      const charge =
                        (tariff?.fclTariffBreakDowns || []).find(
                          (c) =>
                            (c.componentDescription || c.component) === row
                        ) || {};
                      return (
                        <td key={key}>
                          {charge.amount != null ? charge.amount : "-"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </React.Fragment>
          ))}

          {/* Grand total row */}
          <tr className="fw-bold bg-secondary text-white">
            <td>Total Quotation Amount</td>
            {carrierKeys.map((key) => (
              <td key={key}>{totals[key] ?? "-"}</td>
            ))}
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

export default FreightComparison;
